import { Box, Chip, CircularProgress, Button, Stack, useTheme } from '@mui/material';
import React, { useCallback, useMemo, useRef, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';

// Status configuration - defined outside component to prevent recreation
const STATUS_CONFIG = [
    { label: 'ORDER_PENDING', countKey: null },
    { label: 'ORDER_ASSIGNED_TO_SHIPPING_COMPANY', countKey: 'awaitingPickUpCount' },
    { label: 'ORDER_STARTED', countKey: 'currentlyShippingCount' },
    { label: 'ORDER_ON_HOLD', countKey: 'shipmentOnHoldCount' },
    { label: 'ORDER_COMPLETED', countKey: null },
    { label: 'ORDER_CANCELED', countKey: null },
    { label: 'ORDER_RETURN', countKey: 'returnedShipmentCount' },
    { label: 'ALL_ORDERS', countKey: null },
];

/**
 * Individual Tab Button - Memoized to prevent unnecessary re-renders
 */
const TabButton = memo(function TabButton({
    label,
    countKey,
    countValue,
    isSelected,
    isLoading,
    onClick,
    index,
}) {
    const { t } = useTranslation();
    const theme = useTheme();

    const handleClick = useCallback(() => {
        onClick(index, label);
    }, [onClick, index, label]);

    return (
        <Button
            onClick={handleClick}
            sx={{
                textTransform: 'none',
                minWidth: 'auto',
                borderRadius: 0,
                py: 1.5,
                px: 2,
                position: 'relative',
                color: isSelected
                    ? theme.palette.secondary.main
                    : theme.palette.text.primary,
                backgroundColor: 'transparent',
                borderBottom: isSelected
                    ? `2px solid ${theme.palette.secondary.main}`
                    : '2px solid transparent',
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
                whiteSpace: 'nowrap',
                flexShrink: 0,
            }}
            role="tab"
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
            id={`simple-tab-${index}`}
            aria-controls={`simple-tabpanel-${index}`}
        >
            <Box display="flex" alignItems="center" gap={1}>
                {t(`mapit-ship-status.${label}`)}
                {countKey && (
                    isLoading ? (
                        <CircularProgress size={16} />
                    ) : (
                        <Chip label={countValue ?? 0} size="small" />
                    )
                )}
            </Box>
        </Button>
    );
});

/**
 * ShipmentStatusTabs - Optimized as a fully controlled component
 * 
 * Performance improvements:
 * 1. Removed internal selectedTab state - now fully controlled by `value` prop
 * 2. Memoized individual tab buttons
 * 3. Used refs for scroll behavior (doesn't cause re-renders)
 * 4. Stable callback references
 */
function ShipmentStatusTabs({
    value,
    onChange,
    ordersCount,
    ordersCountLoading,
}) {
    const theme = useTheme();
    const tabsContainerRef = useRef(null);
    const hasScrolledRef = useRef(false);

    // Compute selected index from value prop
    const selectedIndex = useMemo(() => {
        const index = STATUS_CONFIG.findIndex((s) => s.label === value);
        return index >= 0 ? index : 0;
    }, [value]);

    // Scroll selected tab into view
    const scrollToTab = useCallback((tabIndex) => {
        const container = tabsContainerRef.current;
        if (!container) return;

        const selectedButton = container.children[tabIndex];
        if (!selectedButton) return;

        const containerRect = container.getBoundingClientRect();
        const buttonRect = selectedButton.getBoundingClientRect();

        const isRTL = theme.direction === 'rtl';
        const isOutsideLeft = buttonRect.left < containerRect.left;
        const isOutsideRight = buttonRect.right > containerRect.right;

        if (isOutsideLeft || isOutsideRight) {
            const scrollLeft = isRTL
                ? selectedButton.offsetLeft - container.clientWidth + selectedButton.offsetWidth + 16
                : Math.max(0, selectedButton.offsetLeft - 16);

            container.scrollTo({
                left: scrollLeft,
                behavior: 'smooth',
            });
        }
    }, [theme.direction]);

    // Scroll to selected tab on mount (once)
    useEffect(() => {
        if (!hasScrolledRef.current && selectedIndex >= 0) {
            hasScrolledRef.current = true;
            // Small delay to ensure DOM is ready
            requestAnimationFrame(() => {
                scrollToTab(selectedIndex);
            });
        }
    }, [selectedIndex, scrollToTab]);

    // Handle tab click - immediately update UI, then notify parent
    const handleTabClick = useCallback((index, label) => {
        // Scroll to the new tab immediately
        requestAnimationFrame(() => {
            scrollToTab(index);
        });

        // Notify parent of the change
        onChange({ target: { name: 'status', value: label } });
    }, [onChange, scrollToTab]);

    return (
        <Box mb={1.75}>
            <Stack
                ref={tabsContainerRef}
                direction="row"
                spacing={0}
                sx={{
                    overflowX: 'auto',
                    // Visible (thin) scrollbar so tabs that don't fit on small laptops stay discoverable
                    scrollbarWidth: 'thin',
                }}
                role="tablist"
                aria-label="Order status tabs"
            >
                {STATUS_CONFIG.map((tab, index) => (
                    <TabButton
                        key={tab.label}
                        label={tab.label}
                        countKey={tab.countKey}
                        countValue={tab.countKey ? ordersCount?.[tab.countKey] : null}
                        isSelected={selectedIndex === index}
                        isLoading={ordersCountLoading}
                        onClick={handleTabClick}
                        index={index}
                    />
                ))}
            </Stack>
        </Box>
    );
}

export default memo(ShipmentStatusTabs);
